/* Tarifa pública de Tabularis.
   ─────────────────────────────────────────────────────────────────────────
   ESPEJO de backend/src/lib/planes.ts. Si cambias un precio acá, cámbialo
   allá: el servidor es el que cobra de verdad, esto es solo lo que se muestra
   en la página. Se duplica a propósito para que la página de precios no
   dependa de que la API responda.

   Nov-2026: tres planes cerrados según cuántos abogados son. El captador no
   ocupa cupo y el procurador viene incluido, dos por abogado. Más de 5
   abogados se conversa. */

export type Ciclo = 'mensual' | 'semestral' | 'anual';

export const CICLOS: Record<Ciclo, { meses: number; descuento: number; etiqueta: string; nota: string }> = {
  mensual: { meses: 1, descuento: 0, etiqueta: 'Mensual', nota: '' },
  semestral: { meses: 6, descuento: 0.1, etiqueta: 'Semestral', nota: '10% menos' },
  anual: { meses: 12, descuento: 0.2, etiqueta: 'Anual', nota: '2 meses gratis' },
};

/** Los planes del panel, de menor a mayor. `abogados` es el tope del plan. */
export const PLANES_PANEL = [
  { abogados: 1, procuradores: 2, precio: 19990, etiqueta: '1 abogado' },
  { abogados: 3, procuradores: 6, precio: 29990, etiqueta: 'Hasta 3 abogados' },
  { abogados: 5, procuradores: 10, precio: 49990, etiqueta: 'Hasta 5 abogados' },
] as const;

/** Más que esto se conversa: no hay precio publicado. */
export const MAX_ABOGADOS_PUBLICO = PLANES_PANEL[PLANES_PANEL.length - 1].abogados;

/** El plan que le toca a un estudio de `abogados` abogados. */
export const planPara = (abogados: number) =>
  PLANES_PANEL.find((p) => abogados <= p.abogados) ?? PLANES_PANEL[PLANES_PANEL.length - 1];

export function precioMensual(abogados: number): number {
  return planPara(Math.max(1, Math.floor(abogados))).precio;
}

const redondear = (n: number) => Math.round(n / 10) * 10;

export function cobro(abogados: number, ciclo: Ciclo) {
  const { meses, descuento } = CICLOS[ciclo];
  const mensual = precioMensual(abogados);
  const sinDescuento = mensual * meses;
  const total = redondear(sinDescuento * (1 - descuento));
  return {
    meses,
    totalClp: total,
    sinDescuentoClp: sinDescuento,
    ahorroClp: sinDescuento - total,
    mensualEquivalenteClp: redondear(total / meses),
  };
}

/* ── Chatbularis IA ──────────────────────────────────────────────────
   Prepago: el estudio carga saldo y cada consulta descuenta lo que cuesta,
   IVA incluido. ESPEJO de lib/precioIA.ts y lib/banchile.ts del backend (precio
   típico por modo, bienvenida y montos de carga): si cambian allá, se
   cambian acá. */
export const CHATBULARIS = {
  /** Saldo de regalo con que parte cada estudio. */
  bienvenidaClp: 5000,
  /** Lo que se puede cargar, con su saldo de regalo. */
  cargas: [
    { montoClp: 5000, bonoClp: 0 },
    { montoClp: 10000, bonoClp: 0 },
    { montoClp: 25000, bonoClp: 0 },
    { montoClp: 50000, bonoClp: 5000 },
    { montoClp: 100000, bonoClp: 10000 },
  ],
  /** Precio típico (mediana medida) y alto (9 de cada 10 cuestan menos) de una
   *  consulta, por modo. */
  redactar: { tipicoClp: 370, hastaClp: 1010 },
  profundo: { tipicoClp: 1640, hastaClp: 4050 },
};

/** Qué contrata el estudio al registrarse: el panel (con Chatbularis prepago,
 *  que tienen todos) o solo Chatbularis. "ambos" queda por los enlaces viejos
 *  y es lo mismo que "tabularis". */
export type Producto = 'tabularis' | 'ambos' | 'chatbularis';

/** Lo que cuesta el panel por ciclo para un estudio nuevo. Chatbularis no
 *  entra: es prepago. */
export function cobroTotal(abogados: number, ciclo: Ciclo, producto: Producto) {
  const c = cobro(abogados, ciclo);
  if (producto === 'chatbularis') return { ...c, totalClp: 0, sinDescuentoClp: 0, ahorroClp: 0, mensualEquivalenteClp: 0, panelClp: 0, mensualClp: 0 };
  return { ...c, panelClp: c.totalClp, mensualClp: precioMensual(abogados) };
}

export const clp = (n: number) => '$' + Math.round(n).toLocaleString('es-CL');
